"use client";

import React from "react";
import { Search, Loader, CircleCheck, CircleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function CustomSourcingFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-10 text-center space-y-4">
        <div className="size-16 rounded-full bg-primary/20 flex items-center justify-center mb-4">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-xl font-semibold text-foreground">Request Received</h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" {...field} />
              </FormControl>
              <FormDescription>Enter your name as it appears on official documents.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone Number is required",
        pattern: {
          value: /^\+?[0-9\s\-()]{7,20}$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+91-9876543210" {...field} />
              </FormControl>
              <FormDescription>Include country code for international callers.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel>Company / Organization (Optional)</FormLabel>
              <FormControl>
                <Input placeholder="Acme Startup Pvt Ltd" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="industry" rules={{
        required: "Industry is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Industry / Category</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select your industry" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {["FMCG", "Natural Wellness", "Sustainability", "Agriculture", "D2C", "Technology", "Other"].map((option, i) => <SelectItem key={option} value={option} data-index={i}>
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="budget_range" render={({
        field
      }) => <FormItem>
              <FormLabel>Budget Range (INR) (Optional)</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select your budget" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {["Below 1,00,000", "1,00,000 – 5,00,000", "5,00,000 – 10,00,000", "Above 10,00,000"].map((option, i) => <SelectItem key={option} value={option} data-index={i}>
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="timeline" render={({
        field
      }) => <FormItem className="md:col-span-2">
              <FormLabel>Preferred Timeline (Optional)</FormLabel>
              <FormControl>
                <Input type="date" {...field} />
              </FormControl>
              <FormDescription>When do you need the trademark sourced by?</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="trademark_description" rules={{
        required: "Trademark Details are required"
      }} render={({
        field
      }) => <FormItem className="md:col-span-2">
              <FormLabel>Desired Trademark Details</FormLabel>
              <FormControl>
                <Textarea placeholder="Describe the type of brand name, product/service focus, tone, any keywords, etc." className="min-h-[120px] resize-y" {...field} />
              </FormControl>
              <FormDescription>
                The more detail you provide, the better we can source suitable trademarks.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 mb-6 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
          <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive-foreground">{submitError}</p>
        </div>}

      <div className="flex justify-end pt-4 border-t border-border mt-4">
        <Button type="submit" disabled={isSubmitting} size="lg" className="w-full sm:w-auto">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Submitting Request...
            </> : "Submit Sourcing Request"}
        </Button>
      </div>
    </Form>;
}
export default function CustomSourcing() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="custom-sourcing" className="dark bg-background text-foreground py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-5xl h-full pointer-events-none opacity-30">
        <div className="absolute top-1/4 left-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-80 h-80 bg-secondary/10 rounded-full blur-3xl" />
      </div>

      <div ref={ref} className={`max-w-4xl mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="relative bg-card/40 backdrop-blur-md border-2 border-secondary rounded-2xl p-8 md:p-16 text-center shadow-2xl overflow-hidden">
          {}
          <div className="absolute inset-0 border border-white/5 rounded-2xl pointer-events-none" />
          
          <div className="relative z-10 flex flex-col items-center">
            <div className="size-16 rounded-2xl bg-secondary/20 flex items-center justify-center mb-6 border border-secondary/30">
              <Search className="size-8 text-primary" />
            </div>
            
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6 text-foreground">
              Don't see the right name?
            </h2>
            
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
              Our curated catalogue is just the beginning. Let our advisory team conduct a custom search across off-market portfolios to find the perfect, legally-vetted trademark tailored to your specific industry and brand vision.
            </p>

            <Dialog>
              <DialogTrigger asChild>
                <Button size="lg" className="text-base px-8 h-14 shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-all duration-300">
                  Inquire for Custom Search
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader className="mb-4">
                  <DialogTitle className="text-2xl">Custom Sourcing Request</DialogTitle>
                  <DialogDescription>
                    Provide details about your ideal brand name, and our team will begin a targeted search.
                  </DialogDescription>
                </DialogHeader>
                
                <FormProvider formId="custom_sourcing_form_our_catalogue" sectionName="Custom Sourcing" defaultValues={{
                full_name: "",
                email: "",
                phone: "",
                company: "",
                industry: "",
                trademark_description: "",
                budget_range: "",
                timeline: ""
              }} validationRules={{
                full_name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: true,
                  pattern: "phone"
                },
                industry: {
                  required: true
                },
                trademark_description: {
                  required: true
                }
              }} submitText="Submit Request" successMessage="Thank you for your custom sourcing request. Our advisory team will review your requirements and contact you within 24-48 hours with potential matches.">
                  <CustomSourcingFormInner />
                </FormProvider>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </section>;
}